import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import type { TeamStablefordPayload } from '@golf-app/shared';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { TeamAvatar } from '@/components/TeamAvatar';
import { teamStablefordQuota } from '@/lib/teamStablefordQuota';
import {
  firstIncompleteStablefordHole,
  resolveStablefordScoreTeamId,
  stablefordScoreEntryParticipants,
  stablefordScorableTeamIds,
} from '@/lib/teamStablefordScoringGroups';

function signed(value: number, digits = 0): string {
  const rounded = Number(value.toFixed(digits));
  return `${rounded > 0 ? '+' : ''}${rounded}`;
}

export default function TeamStablefordScoring() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TeamStablefordPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentHole, setCurrentHole] = useState(1);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [activeScoreTeamId, setActiveScoreTeamId] = useState<string | null>(null);
  const activeScoreTeamIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!roundId) return;
    try {
      const payload = await api.getTeamStableford(roundId);
      setData(payload);
      if (payload.holes.length) {
        const scoreTeamId = resolveStablefordScoreTeamId(payload, activeScoreTeamIdRef.current);
        activeScoreTeamIdRef.current = scoreTeamId;
        setActiveScoreTeamId(scoreTeamId);
        const firstIncompleteHole = firstIncompleteStablefordHole(payload, scoreTeamId);
        setCurrentHole((current) => firstIncompleteHole
          ?? (payload.holes.some((hole) => hole.holeNumber === current)
            ? current
            : payload.holes[0]!.holeNumber));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Could not load Team Stableford.');
    } finally {
      setLoading(false);
    }
  }, [roundId]);

  useEffect(() => { load(); }, [load]);

  const currentHoleInfo = useMemo(
    () => data?.holes.find((hole) => hole.holeNumber === currentHole) ?? null,
    [data, currentHole],
  );

  useEffect(() => {
    if (!data || !currentHoleInfo) return;
    const saved = new Map(
      data.holeScores
        .filter((score) => score.holeNumber === currentHoleInfo.holeNumber)
        .map((score) => [score.eventPlayerId, score.grossScore]),
    );
    const next: Record<string, string> = {};
    for (const player of data.participants) {
      next[player.eventPlayerId] = String(saved.get(player.eventPlayerId) ?? currentHoleInfo.par);
    }
    setScores(next);
  }, [data, currentHoleInfo]);

  const playersByTeam = useMemo(() => {
    const grouped = new Map<string, TeamStablefordPayload['participants']>();
    for (const participant of data?.participants ?? []) {
      const group = grouped.get(participant.teamId) ?? [];
      group.push(participant);
      grouped.set(participant.teamId, group);
    }
    return grouped;
  }, [data]);

  const standingsByPlayer = useMemo(
    () => new Map((data?.playerStandings ?? []).map((standing) => [standing.eventPlayerId, standing])),
    [data],
  );

  const scoreTeamIds = useMemo(
    () => (data ? stablefordScorableTeamIds(data) : null),
    [data],
  );
  const activeScoreTeam = useMemo(
    () => data?.teams.find((team) => team.id === activeScoreTeamId) ?? null,
    [activeScoreTeamId, data],
  );
  const entryParticipants = useMemo(
    () => (data ? stablefordScoreEntryParticipants(data, activeScoreTeamId) : []),
    [activeScoreTeamId, data],
  );
  const entryTeams = useMemo(() => {
    if (!data || !scoreTeamIds?.length || !activeScoreTeamId) return data?.teams ?? [];
    return data.teams.filter((team) => team.id === activeScoreTeamId);
  }, [activeScoreTeamId, data, scoreTeamIds]);

  function selectScoreTeam(teamId: string) {
    if (!data || !scoreTeamIds?.includes(teamId)) return;
    activeScoreTeamIdRef.current = teamId;
    setActiveScoreTeamId(teamId);
    const firstIncompleteHole = firstIncompleteStablefordHole(data, teamId);
    if (firstIncompleteHole != null) setCurrentHole(firstIncompleteHole);
    setError(null);
  }

  async function saveHole() {
    if (!data || !roundId || !currentHoleInfo) return;
    const payload: Array<{ eventPlayerId: string; grossScore: number }> = [];
    if (entryParticipants.length === 0) {
      setError('This Stableford group has no players to score.');
      return;
    }
    for (const player of entryParticipants) {
      const raw = (scores[player.eventPlayerId] ?? '').trim();
      const grossScore = Number(raw);
      if (!Number.isInteger(grossScore) || grossScore < 1 || grossScore > 20) {
        setError(`Enter a whole-number score from 1 to 20 for ${player.displayName}.`);
        return;
      }
      payload.push({ eventPlayerId: player.eventPlayerId, grossScore });
    }
    setSaving(true);
    setError(null);
    try {
      const next = await api.saveTeamStablefordHole(roundId, currentHoleInfo.holeNumber, payload);
      setData(next);
      const currentIndex = next.holes.findIndex((hole) => hole.holeNumber === currentHoleInfo.holeNumber);
      if (currentIndex >= 0 && currentIndex < next.holes.length - 1) setCurrentHole(next.holes[currentIndex + 1]!.holeNumber);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save this hole.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <AppShell back={{ to: `/app/tournament-rounds/${roundId}/matches`, label: 'Round' }}><div className="t-loading"><div className="spinner" /></div></AppShell>;
  }
  if (!data) {
    return <AppShell back={{ to: `/app/tournament-rounds/${roundId}/matches`, label: 'Round' }}><div className="t-empty"><div className="t-empty-title">Stableford round not found</div><div className="t-empty-hint">{error ?? 'The round could not be loaded.'}</div></div></AppShell>;
  }

  const teamCap = data.config.teamRules?.playerContributionLimit ?? 4;
  const individualBoard = [...data.playerStandings]
    .sort((left, right) => right.netPoints - left.netPoints || left.eventPlayerId.localeCompare(right.eventPlayerId));
  const activeIndex = data.holes.findIndex((hole) => hole.holeNumber === currentHole);

  return (
    <AppShell back={{ to: `/app/tournaments/${data.round.eventId}/rounds/${data.round.id}`, label: data.round.name }}>
      <section className="t-hero">
        <h1>Team Stableford</h1>
        <p>
          Each group enters its own gross scores. {data.config.points.albatrossOrBetter}/{data.config.points.eagle}/{data.config.points.birdie}/{data.config.points.par}/{data.config.points.bogey}/{data.config.points.doubleBogeyOrWorse}
          {' '}points for albatross through double-or-worse. Each player’s team contribution is capped at {signed(teamCap)}.
        </p>
      </section>

      {error ? <div className="t-error" role="alert">{error}</div> : null}

      {!data.setupReady ? (
        <div className="t-empty">
          <div className="t-empty-title">Team roster not set up</div>
          <div className="t-empty-hint">Return to round setup and snapshot the teams before locking Stableford for scoring.</div>
        </div>
      ) : <>
        <section className="scorecard-standings" aria-label="Team Stableford standings">
          <div className="scorecard-standings-heading">
            <div><span className="t-label">Team leaderboard</span><p>Capped team results. Highest total wins.</p></div>
            <Trophy aria-hidden="true" />
          </div>
          <div className="scorecard-standings-list">
            {data.teamStandings.map((team, index) => (
              <div className="scorecard-standing-row" key={team.teamId}>
                <span className="scorecard-standing-rank">{index + 1}</span>
                <TeamAvatar team={data.teams.find((candidate) => candidate.id === team.teamId) ?? { id: team.teamId, name: team.name, shortName: null, logoUrl: null, primaryColor: team.primaryColor }} size={30} />
                <span className="scorecard-standing-name">{team.name}<small>{team.playerCount} players · {team.grossPoints} gross pts</small></span>
                <strong className="scorecard-standing-points">{signed(team.points, 1)}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="scorecard-standings" aria-label="Best net Stableford players">
          <div className="scorecard-standings-heading"><div><span className="t-label">Best net players</span><p>Uncapped, for individual prizes.</p></div></div>
          <div className="scorecard-standings-list">
            {individualBoard.slice(0, 5).map((standing, index) => {
              const player = data.participants.find((candidate) => candidate.eventPlayerId === standing.eventPlayerId)!;
              const quota = teamStablefordQuota(data.config.points.par, data.holes.length, player.playingHandicap);
              return <div className="scorecard-standing-row" key={standing.eventPlayerId}>
                <span className="scorecard-standing-rank">{index + 1}</span>
                <PlayerAvatar avatarUrl={player.avatarUrl} displayName={player.displayName} size={30} />
                <span className="scorecard-standing-name">
                  {player.displayName}
                  <small>
                    Quota {quota}
                    {standing.holesPlayed > 0 ? ` · pace target ${standing.targetPoints.toFixed(1)}` : ''}
                    {' · '}{standing.points} gross pts
                  </small>
                </span>
                <strong className="scorecard-standing-points">{signed(standing.netPoints, 1)}</strong>
              </div>;
            })}
          </div>
        </section>

        {currentHoleInfo ? <section className="scorecard-entry">
          {scoreTeamIds && scoreTeamIds.length > 0 ? (
            <div className="scorecard-group-picker">
              <div>
                <span className="t-label">{scoreTeamIds.length > 1 ? 'Scoring group' : 'Your scoring group'}</span>
                <p>Enter the gross score for everyone in this group, then save the hole.</p>
              </div>
              <div className="t-chip-row" aria-label="Stableford scoring group">
                {data.teams
                  .filter((team) => scoreTeamIds.includes(team.id))
                  .map((team) => (
                    <button
                      type="button"
                      key={team.id}
                      className={`t-chip${team.id === activeScoreTeamId ? ' t-chip-active' : ''}`}
                      aria-pressed={team.id === activeScoreTeamId}
                      onClick={() => selectScoreTeam(team.id)}
                    >
                      {team.name}
                    </button>
                  ))}
              </div>
            </div>
          ) : null}
          <div className="scorecard-entry-heading">
            <div><span className="t-label">Hole {currentHoleInfo.holeNumber}</span><h2>Par {currentHoleInfo.par} · HCP {currentHoleInfo.strokeIndex}</h2></div>
            <div className="t-chip-row">
              <button type="button" className="t-chip" disabled={activeIndex <= 0} onClick={() => setCurrentHole(data.holes[activeIndex - 1]!.holeNumber)}><ChevronLeft /> Prev</button>
              <button type="button" className="t-chip" disabled={activeIndex >= data.holes.length - 1} onClick={() => setCurrentHole(data.holes[activeIndex + 1]!.holeNumber)}>Next <ChevronRight /></button>
            </div>
          </div>
          {entryTeams.map((team) => {
            const players = playersByTeam.get(team.id) ?? [];
            if (!players.length) return null;
            return <div className="scorecard-score-list" key={team.id}>
              <div className="t-label" style={{ color: team.primaryColor ?? 'var(--text-sec)' }}>{team.name}</div>
              {players.map((player) => {
                const standing = standingsByPlayer.get(player.eventPlayerId);
                const quota = teamStablefordQuota(data.config.points.par, data.holes.length, player.playingHandicap);
                return <label className="scorecard-score-row" key={player.eventPlayerId}>
                  <PlayerAvatar avatarUrl={player.avatarUrl} displayName={player.displayName} size={30} />
                  <span className="scorecard-score-name">
                    {player.displayName}
                    <small>HCP {player.playingHandicap} · quota {quota} · {signed(standing?.netPoints ?? 0, 1)} net · team {signed(standing?.teamContribution ?? 0, 1)}</small>
                  </span>
                  <input className="scorecard-score-input" inputMode="numeric" value={scores[player.eventPlayerId] ?? ''} disabled={!data.permissions.canScore || saving} onChange={(event) => setScores((current) => ({ ...current, [player.eventPlayerId]: event.target.value }))} aria-label={`${player.displayName} gross score`} />
                </label>;
              })}
            </div>;
          })}
          {data.permissions.canScore
            ? <button type="button" className="t-btn t-btn-primary" disabled={saving} onClick={saveHole}>
                {saving
                  ? 'Saving…'
                  : activeScoreTeam
                    ? `Save Group Scores · Hole ${currentHoleInfo.holeNumber}`
                    : `Save Hole ${currentHoleInfo.holeNumber}`}
              </button>
            : <p className="t-hint">
                {['completed', 'cancelled'].includes(data.round.status)
                  ? 'Scoring is closed for this round.'
                  : scoreTeamIds && scoreTeamIds.length > 0
                    ? data.permissions.canManage
                      ? 'Lock the round for scoring when the teams and quotas are final.'
                      : 'The organizer needs to lock the round before group scoring starts.'
                    : 'Only a linked player in a Stableford group, the event owner, or a captain can enter scores.'}
              </p>}
        </section> : null}
      </>}
    </AppShell>
  );
}
